import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Chip, 
  CircularProgress, 
  Button, 
  TextField, 
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Snackbar,
  Alert,
  Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RiskMatrix from './components/RiskMatrix';
import api from '../../services/api';

const ControlsTreatment = () => {
  const [risks, setRisks] = useState([]);
  const [supplementalControls, setSupplementalControls] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal States
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [newControl, setNewControl] = useState({
    control_measure: '',
    action: '',
    effectiveness: 'Effective',
    strategy: 'Reduce',
    owner: '',
    residual_likelihood: 'POSSIBLE',
    residual_consequence: 'MODERATE'
  });

  // Feedback
  const [feedback, setFeedback] = useState({ open: false, msg: '', severity: 'success' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const risksRes = await api.get('/risks');
      const risksData = risksRes.data;
      setRisks(risksData);

      // Fetch all supplemental controls
      const controlsRes = await api.get('/risk-controls');
      const controlsData = controlsRes.data;
      
      // Group controls by risk_id
      const grouped = {};
      controlsData.forEach(c => {
        if (!grouped[c.risk_id]) grouped[c.risk_id] = [];
        grouped[c.risk_id].push(c);
      });
      setSupplementalControls(grouped);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getScoreLabel = (likelihood, consequence) => {
    const likelihoodMap = { 'RARE': 0, 'UNLIKELY': 1, 'POSSIBLE': 2, 'LIKELY': 3, 'ALMOST CERTAIN': 4 };
    const matrix = {
        'CATASTROPHIC': ['TOLERABLE', 'HIGH', 'EXTREME', 'EXTREME', 'EXTREME'],
        'MAJOR': ['LOW', 'TOLERABLE', 'HIGH', 'EXTREME', 'EXTREME'],
        'MODERATE': ['LOW', 'LOW', 'TOLERABLE', 'HIGH', 'HIGH'],
        'MINOR': ['VERY LOW', 'LOW', 'TOLERABLE', 'TOLERABLE', 'HIGH'],
        'INSIGNIFICANT': ['VERY LOW', 'VERY LOW', 'LOW', 'TOLERABLE', 'TOLERABLE']
    };
    const lKey = likelihoodMap[String(likelihood).toUpperCase()];
    const cKey = String(consequence).toUpperCase();
    return matrix[cKey]?.[lKey] || 'N/A';
  };

  const getScoreColor = (label) => {
    const colors = {
        'EXTREME': '#ef4444',
        'HIGH': '#f97316',
        'TOLERABLE': '#eab308',
        'LOW': '#84cc16',
        'VERY LOW': '#15803d',
        'N/A': '#475569'
    };
    return colors[label] || '#94a3b8';
  };

  const getRevisedRating = (risk, controls, newEffectiveness = null) => {
    if (!risk) return 'N/A';
    
    const likelihoods = ['RARE', 'UNLIKELY', 'POSSIBLE', 'LIKELY', 'ALMOST CERTAIN'];
    const weights = { 
        'STRONG': 2, 'EFFECTIVE': 1, 'MODERATE': 0.5, 'WEAK': 0.2, 'INEFFECTIVE': 0,
        'TOLERABLE': 1, 'ADEQUATE': 1, 'EXCELLENT': 2, 'GOOD': 1, 'VERY STRONG': 2.5
    };
    
    const getWeight = (label) => {
        if (!label) return 0;
        const normalized = String(label).toUpperCase().trim();
        return weights[normalized] || 0;
    };
    
    // 1. Start with Inherent Likelihood index
    let idx = likelihoods.indexOf(String(risk.inherent_likelihood).toUpperCase());
    if (idx === -1) idx = likelihoods.indexOf(String(risk.residual_likelihood).toUpperCase());
    if (idx === -1) return 'N/A';

    // 2. Sum weights of all controls
    let totalWeight = getWeight(risk.control_effectiveness);
    
    // Add existing supplemental controls
    if (controls && Array.isArray(controls)) {
        controls.forEach(c => {
            totalWeight += getWeight(c.effectiveness);
        });
    }

    // Add the new control effectiveness being selected in modal
    if (newEffectiveness) {
        totalWeight += getWeight(newEffectiveness);
    }

    const totalShift = Math.floor(totalWeight);
    const newIdx = Math.max(0, idx - totalShift);
    
    return getScoreLabel(likelihoods[newIdx], risk.inherent_consequence);
  };

  const likelihoodOptions = [
    { value: 'RARE', label: 'RARE' },
    { value: 'UNLIKELY', label: 'UNLIKELY' },
    { value: 'POSSIBLE', label: 'POSSIBLE' },
    { value: 'LIKELY', label: 'LIKELY' },
    { value: 'ALMOST CERTAIN', label: 'ALMOST CERTAIN' }
  ];

  const consequenceOptions = [
    { value: 'INSIGNIFICANT', label: 'INSIGNIFICANT' },
    { value: 'MINOR', label: 'MINOR' },
    { value: 'MODERATE', label: 'MODERATE' },
    { value: 'MAJOR', label: 'MAJOR' },
    { value: 'CATASTROPHIC', label: 'CATASTROPHIC' }
  ];

  const effectivenessOptions = ['Strong', 'Effective', 'Moderate', 'Weak', 'Ineffective'];

  const handleOpenModal = (risk) => {
    setSelectedRisk(risk);
    setNewControl({
        risk_id: risk.id,
        control_measure: '',
        action: '',
        effectiveness: 'Effective',
        strategy: 'Reduce',
        owner: risk.owner || '',
        deadline: risk.resolved_by || new Date().toISOString().split('T')[0],
        residual_likelihood: risk.residual_likelihood || 'POSSIBLE',
        residual_consequence: risk.residual_consequence || 'MODERATE'
    });
    setModalOpen(true);
  };

  const isOverdue = (deadline, status) => {
    if (!deadline || ['Resolved', 'Closed'].includes(status)) return false;
    return new Date() > new Date(deadline);
  };

  const saveControl = async () => {
    if (!newControl.control_measure || !newControl.action) {
        setFeedback({ open: true, msg: 'Please provide both a description and an action.', severity: 'error' });
        return;
    }

    // Calculate new ratings based on aggregated points
    const likelihoods = ['RARE', 'UNLIKELY', 'POSSIBLE', 'LIKELY', 'ALMOST CERTAIN'];
    const weights = { 
        'STRONG': 2, 'EFFECTIVE': 1, 'MODERATE': 0.5, 'WEAK': 0.2, 'INEFFECTIVE': 0,
        'TOLERABLE': 1, 'ADEQUATE': 1, 'EXCELLENT': 2, 'GOOD': 1, 'VERY STRONG': 2.5
    };
    
    const getWeight = (label) => {
        if (!label) return 0;
        const normalized = String(label).toUpperCase().trim();
        return weights[normalized] || 0;
    };
    
    // Sum existing controls for this risk
    const existing = supplementalControls[selectedRisk.id] || [];
    let totalWeight = getWeight(selectedRisk.control_effectiveness);
    existing.forEach(c => { totalWeight += getWeight(c.effectiveness); });
    
    // Add the new one
    totalWeight += getWeight(newControl.effectiveness);
    
    const totalShift = Math.floor(totalWeight);
    let baseIdx = likelihoods.indexOf(String(selectedRisk.inherent_likelihood).toUpperCase());
    if (baseIdx === -1) baseIdx = 3; // Default to LIKELY if missing

    const newIdx = Math.max(0, baseIdx - totalShift);
    
    const payload = {
        ...newControl,
        residual_likelihood: likelihoods[newIdx],
        residual_consequence: selectedRisk.inherent_consequence // Usually stays same as inherent or previous
    };

    try {
        await api.post('/risk-controls', payload);
        setFeedback({ open: true, msg: 'Control recorded successfully', severity: 'success' });
        setModalOpen(false);
        const riskId = selectedRisk.id; // Store ID before closing modal/fetching
        await fetchData();
        // Scroll to the updated risk
        setTimeout(() => {
            const element = document.getElementById(`risk-${riskId}`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 500);
    } catch (e) {
        setFeedback({ open: true, msg: e.response?.data?.error || 'Failed to save control (Server Error)', severity: 'error' });
    }
  };

  const deleteControl = async (id) => {
    if (!window.confirm('Are you sure you want to remove this supplemental control?')) return;
    try {
        await api.delete(`/risk-controls/${id}`);
        setFeedback({ open: true, msg: 'Control removed', severity: 'success' });
        fetchData();
    } catch (e) {
        setFeedback({ open: true, msg: 'Failed to delete', severity: 'error' });
    }
  };

  const filteredRisks = risks.filter(r => 
    r.sn.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.risk_description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
      <CircularProgress sx={{ color: '#10b981' }} />
    </Box>
  );

  return (
    <Box sx={{ p: 4 }}>
      {/* Header Bar */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" className="gradient-text" sx={{ fontWeight: '900' }}>
            Action Tracking & Controls
        </Typography>
        <TextField
            placeholder="Search by ID or description..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#64748b' }} /></InputAdornment>
            }}
            sx={{ 
                width: 350,
                '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(255,255,255,0.03)',
                    color: 'white',
                    borderRadius: '12px',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' }
                }
            }}
        />
      </Box>

      <Grid container spacing={5}>
        {filteredRisks.map((risk) => {
          const overdue = isOverdue(risk.resolved_by, risk.status);
          const controls = supplementalControls[risk.id] || [];
          const inherentLabel = getScoreLabel(risk.inherent_likelihood, risk.inherent_consequence);
          const residualLabel = getScoreLabel(risk.residual_likelihood, risk.residual_consequence);
          
          return (
            <Grid item xs={12} key={risk.id}>
              <Paper id={`risk-${risk.id}`} sx={{ 
                p: 0, 
                borderRadius: '32px', 
                background: 'rgba(15, 23, 42, 0.6)', 
                backdropFilter: 'blur(20px)',
                border: overdue ? '1px solid rgba(239, 68, 68, 0.4)' : '1px solid rgba(255,255,255,0.08)',
                overflow: 'hidden'
              }}>
                {/* Risk Header */}
                <Box sx={{ p: 2.5, background: 'linear-gradient(90deg, rgba(255,255,255,0.03) 0%, transparent 100%)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={9}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                <Chip label={risk.sn} sx={{ fontWeight: '900', bgcolor: '#3b82f6', color: 'white', borderRadius: '6px', height: '22px', fontSize: '0.7rem' }} />
                                <Typography sx={{ fontWeight: '900', color: 'white', fontSize: '0.9rem' }}>{risk.category}</Typography>
                            </Box>
                            <Typography sx={{ color: '#cbd5e1', fontSize: '0.85rem', lineHeight: 1.4, maxWidth: '98%' }}>
                                {risk.risk_description}
                            </Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={3}>
                            <Box sx={{ 
                                background: 'rgba(0,0,0,0.3)', 
                                p: 1, 
                                borderRadius: '12px', 
                                border: '1px solid rgba(255,255,255,0.05)',
                                display: 'flex',
                                justifyContent: 'space-around',
                                alignItems: 'center',
                                minWidth: '180px'
                            }}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="overline" sx={{ color: '#64748b', fontWeight: '900', letterSpacing: '1px', fontSize: '0.55rem' }}>INHERENT</Typography>
                                    <Typography sx={{ color: getScoreColor(inherentLabel), fontWeight: '900', fontSize: '0.85rem' }}>{inherentLabel}</Typography>
                                </Box>
                                <TrendingDownIcon sx={{ color: '#10b981', fontSize: '1.2rem', opacity: 0.5 }} />
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="overline" sx={{ color: '#64748b', fontWeight: '900', letterSpacing: '1px', fontSize: '0.55rem' }}>RESIDUAL</Typography>
                                    <Typography sx={{ color: getScoreColor(residualLabel), fontWeight: '900', fontSize: '0.85rem' }}>{residualLabel}</Typography>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* Table Section */}
                <Box sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography sx={{ color: 'white', fontWeight: '900', fontSize: '0.9rem' }}>Control Measures</Typography>
                        <Button 
                            variant="outlined"
                            onClick={() => handleOpenModal(risk)}
                            sx={{ 
                                color: '#10b981', 
                                borderColor: '#10b981', 
                                textTransform: 'none', 
                                fontWeight: '900',
                                borderRadius: '8px',
                                px: 2,
                                py: 0.5,
                                fontSize: '0.8rem',
                                borderWidth: '1.5px',
                                '&:hover': { borderWidth: '1.5px', background: 'rgba(16, 185, 129, 0.1)', borderColor: '#10b981' }
                            }}
                        >
                            Add Supplemental Control
                        </Button>
                    </Box>

                    <TableContainer sx={{ background: 'rgba(0,0,0,0.2)', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)', mb: 3 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ '& th': { color: '#94a3b8', fontSize: '0.7rem', fontWeight: '900', borderBottom: '1px solid rgba(255,255,255,0.1)', py: 1.2, textTransform: 'uppercase' } }}>
                                    <TableCell>Ref</TableCell>
                                    <TableCell>Control Measure</TableCell>
                                    <TableCell>Effectiveness</TableCell>
                                    <TableCell>Actions</TableCell>
                                    <TableCell>Mitigation</TableCell>
                                    <TableCell align="right">TARGET RESOLUTION</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {/* Primary Control */}
                                <TableRow sx={{ '& td': { borderBottom: '1px solid rgba(255,255,255,0.05)', py: 1.2, color: '#f1f5f9', fontSize: '0.8rem' } }}>
                                    <TableCell sx={{ color: '#64748b', fontWeight: 'bold' }}>P-001</TableCell>
                                    <TableCell sx={{ maxWidth: 300 }}>{risk.existing_controls || '---'}</TableCell>
                                    <TableCell>
                                        <Chip label={risk.control_effectiveness || 'Effective'} size="small" sx={{ bgcolor: 'rgba(16, 185, 129, 0.1)', color: '#10b981', fontWeight: 'bold', fontSize: '0.65rem' }} />
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={risk.method || 'Reduce'} size="small" sx={{ bgcolor: 'rgba(59, 130, 246, 0.2)', color: '#3b82f6', fontWeight: 'black', fontSize: '0.7rem' }} />
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 250, color: '#94a3b8' }}>{risk.mitigation_strategy || '---'}</TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{risk.resolved_by || '---'}</TableCell>
                                </TableRow>

                                {/* Supplemental Controls */}
                                {controls.map((c, idx) => (
                                    <TableRow key={c.id} sx={{ '& td': { borderBottom: '1px solid rgba(255,255,255,0.05)', py: 1.2, color: '#f1f5f9', fontSize: '0.8rem' } }}>
                                        <TableCell sx={{ color: '#10b981', fontWeight: 'bold' }}>P-{String(idx + 2).padStart(3, '0')}</TableCell>
                                        <TableCell sx={{ maxWidth: 300 }}>{c.control_measure}</TableCell>
                                        <TableCell>
                                            <Chip label={c.effectiveness} size="small" sx={{ bgcolor: `${getScoreColor(c.effectiveness)}20`, color: getScoreColor(c.effectiveness), fontWeight: 'bold', fontSize: '0.65rem' }} />
                                        </TableCell>
                                        <TableCell sx={{ color: '#cbd5e1' }}>{c.action || '---'}</TableCell>
                                        <TableCell>
                                            <Chip label={c.strategy} size="small" sx={{ bgcolor: 'rgba(59, 130, 246, 0.2)', color: '#3b82f6', fontWeight: 'black', fontSize: '0.7rem' }} />
                                        </TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                            <Typography sx={{ color: isOverdue(c.deadline, risk.status) ? '#ef4444' : 'inherit', fontSize: 'inherit' }}>{c.deadline || risk.resolved_by}</Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Matrix & Tracking Section */}
                    <Grid container spacing={3} alignItems="flex-start">
                        <Grid item xs={12} md={5}>
                            <Typography sx={{ color: '#64748b', fontWeight: '900', mb: 1.5, fontSize: '0.7rem', letterSpacing: '1px' }}>
                                RESIDUAL EXPOSURE
                            </Typography>
                            <Box sx={{ bgcolor: 'rgba(255,255,255,0.02)', p: 2, borderRadius: '16px', display: 'flex', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.03)' }}>
                                <RiskMatrix 
                                    selectedLikelihood={risk.residual_likelihood} 
                                    selectedConsequence={risk.residual_consequence} 
                                    title=""
                                />
                            </Box>
                        </Grid>
                        
                        <Grid item xs={12} md={7}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Box>
                                        <Typography variant="overline" sx={{ color: '#64748b', fontWeight: 'bold', display: 'block', mb: 0.5, fontSize: '0.6rem' }}>CURRENT STATUS</Typography>
                                        <Chip 
                                            label={risk.status} 
                                            size="small"
                                            sx={{ bgcolor: risk.status === 'Resolved' ? '#10b981' : '#3b82f6', color: 'white', fontWeight: 'black', height: '28px', fontSize: '0.75rem' }} 
                                        />
                                    </Box>
                                    <Box sx={{ textAlign: 'right' }}>
                                        <Typography variant="overline" sx={{ color: '#64748b', fontWeight: 'bold', display: 'block', mb: 0.5, fontSize: '0.6rem' }}>ESCALATION</Typography>
                                    {overdue ? (
                                        <Chip label="ESCALATED: OVERDUE" size="small" sx={{ bgcolor: '#ef4444', color: 'white', fontWeight: 'black', height: '28px', fontSize: '0.75rem' }} />
                                    ) : (
                                        <Chip label="WITHIN TIMELINES" size="small" sx={{ bgcolor: 'rgba(16, 185, 129, 0.05)', color: '#10b981', border: '1px solid rgba(16, 185, 129, 0.2)', fontWeight: 'bold', height: '28px', fontSize: '0.7rem' }} />
                                    )}
                                    </Box>
                                </Box>
                                
                                <Box sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.02)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.04)' }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 'bold', display: 'block', mb: 0.5, fontSize: '0.6rem' }}>ACTION OWNER</Typography>
                                            <Typography sx={{ color: 'white', fontWeight: '900', fontSize: '0.9rem' }}>{risk.owner}</Typography>
                                        </Grid>
                                        <Grid item xs={6} sx={{ textAlign: 'right' }}>
                                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 'bold', display: 'block', mb: 0.5, fontSize: '0.6rem' }}>TARGET RESOLUTION</Typography>
                                            <Typography sx={{ color: overdue ? '#ef4444' : '#10b981', fontWeight: '900', fontSize: '0.9rem' }}>{risk.resolved_by || '---'}</Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* Add Control Modal */}
      <Dialog 
        open={modalOpen} 
        onClose={() => setModalOpen(false)}
        PaperProps={{
            sx: { background: '#1e293b', color: 'white', borderRadius: '20px', minWidth: '550px', p: 1 }
        }}
      >
        <DialogTitle sx={{ fontWeight: '900', fontSize: '1.2rem', pb: 0 }}>Add Supplemental Control</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box>
                    <Typography sx={{ color: '#94a3b8', fontWeight: 'bold', mb: 1, fontSize: '0.7rem' }}>CONTROL MEASURE DESCRIPTION</Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={2}
                        placeholder="Describe the action to be taken..."
                        value={newControl.control_measure}
                        onChange={(e) => setNewControl({...newControl, control_measure: e.target.value})}
                        sx={{ '& .MuiOutlinedInput-root': { color: 'white', bgcolor: 'rgba(0,0,0,0.2)', fontSize: '0.85rem' } }}
                    />
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography sx={{ color: '#94a3b8', fontWeight: 'bold', mb: 1, fontSize: '0.7rem' }}>MONITORING METHOD</Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            placeholder="Specify monitoring method..."
                            value={newControl.action}
                            onChange={(e) => setNewControl({...newControl, action: e.target.value})}
                            sx={{ '& .MuiOutlinedInput-root': { color: 'white', bgcolor: 'rgba(0,0,0,0.2)', fontSize: '0.85rem' } }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Typography sx={{ color: '#94a3b8', fontWeight: 'bold', mb: 0.5, fontSize: '0.7rem' }}>CONTROL ADEQUACY</Typography>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            value={newControl.effectiveness}
                            onChange={(e) => setNewControl({...newControl, effectiveness: e.target.value})}
                            sx={{ '& .MuiOutlinedInput-root': { color: 'white', bgcolor: 'rgba(0,0,0,0.2)', fontSize: '0.85rem' } }}
                            SelectProps={{ MenuProps: { PaperProps: { sx: { bgcolor: '#1e293b', color: 'white' } } } }}
                        >
                            {effectivenessOptions.map(opt => <MenuItem key={opt} value={opt} sx={{ fontSize: '0.85rem' }}>{opt}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography sx={{ color: '#94a3b8', fontWeight: 'bold', mb: 0.5, fontSize: '0.7rem' }}>STRATEGY</Typography>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            value={newControl.strategy}
                            onChange={(e) => setNewControl({...newControl, strategy: e.target.value})}
                            sx={{ '& .MuiOutlinedInput-root': { color: 'white', bgcolor: 'rgba(0,0,0,0.2)', fontSize: '0.85rem' } }}
                            SelectProps={{ MenuProps: { PaperProps: { sx: { bgcolor: '#1e293b', color: 'white' } } } }}
                        >
                            {['Avoid', 'Reduce', 'Transfer', 'Accept'].map(opt => <MenuItem key={opt} value={opt} sx={{ fontSize: '0.85rem' }}>{opt}</MenuItem>)}
                        </TextField>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography sx={{ color: '#94a3b8', fontWeight: 'bold', mb: 0.5, fontSize: '0.7rem' }}>OWNER</Typography>
                        <TextField
                            fullWidth
                            size="small"
                            value={newControl.owner}
                            onChange={(e) => setNewControl({...newControl, owner: e.target.value})}
                            sx={{ '& .MuiOutlinedInput-root': { color: 'white', bgcolor: 'rgba(0,0,0,0.2)', fontSize: '0.85rem' } }}
                        />
                    </Grid>
                </Grid>
            </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
            <Button 
                variant="outlined"
                onClick={() => setModalOpen(false)} 
                sx={{ 
                    color: '#94a3b8', 
                    borderColor: 'rgba(148, 163, 184, 0.3)',
                    textTransform: 'none', 
                    fontWeight: 'bold', 
                    fontSize: '0.85rem',
                    borderRadius: '10px',
                    '&:hover': { borderColor: '#94a3b8', color: 'white' }
                }}
            >
                Discard
            </Button>
            <Button 
                variant="contained" 
                onClick={saveControl}
                sx={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', borderRadius: '10px', fontWeight: '900', px: 3, fontSize: '0.85rem' }}
            >
                Confirm Control
            </Button>
        </DialogActions>
      </Dialog>

      {/* Alerts */}
      <Snackbar 
        open={feedback.open} 
        autoHideDuration={4000} 
        onClose={() => setFeedback({ ...feedback, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={feedback.severity} variant="filled" sx={{ width: '100%', borderRadius: '12px' }} onClose={() => setFeedback({ ...feedback, open: false })}>
            {feedback.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ControlsTreatment;
